import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const noteVariants = cva("", {
  variants: {
    // `inherit`: the surrounding colour, for a state that tints its whole row (a live meeting).
    // `destructive`: a caption that is the problem itself (a character count over its limit), FormField's error token.
    tone: {
      muted: "text-muted-foreground",
      inherit: "",
      destructive: "text-destructive-text",
    },
    size: {
      xs: "text-xs",
      // The Focused card's meta line (BRE-9279).
      meta: "text-meta",
      sm: "text-sm",
      // The surrounding text's size: a title's placeholder stays title-sized.
      inherit: "",
    },
    // `empty`: a message standing in for missing content ("No data to plot."), the one place text is italic.
    variant: {
      default: "",
      empty: "italic",
    },
    // `normal`: subtext inside a bold row (a selected nav item) stays regular weight.
    weight: {
      default: "",
      medium: "font-medium",
      normal: "font-normal",
    },
  },
  defaultVariants: { tone: "muted", size: "xs", variant: "default", weight: "default" },
});

export interface NoteProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof noteVariants> {
  as?: "p" | "span" | "div";
}

/** Muted text: help under a field, a row's subtext, a caption, or an inline empty message (`variant="empty"`). */
const Note = React.forwardRef<HTMLElement, NoteProps>(({ as = "p", className, tone, size, variant, weight, ...props }, ref) => {
  // One tag type for the checker: the three tags take the same attributes.
  const Comp = as as "p";
  return <Comp ref={ref as React.Ref<HTMLParagraphElement>} className={cn(noteVariants({ tone, size, variant, weight }), className)} {...props} />;
});
Note.displayName = "Note";

export { Note, noteVariants };
